import {useEffect, useState} from 'react'
import {useParams, useNavigate} from 'react-router'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import SkeletonCards from '@/customComponents/editRideComponents/SkeletonCards.jsx'
import DestinationSection from '@/customComponents/editRideComponents/PageModals/DestinationSection.jsx'
import SmartPickupFlow from '@/customComponents/sharedComponents/smartTime/SmartPickupFlow.jsx'
import CarSection from '@/customComponents/editRideComponents/PageModals/CarSection.jsx'
import PassengerSection from '@/customComponents/editRideComponents/PageModals/PassengerSection.jsx'
import LuggageSection from '@/customComponents/editRideComponents/PageModals/LuggageSection.jsx'
import SpecialRequestPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SpecialRequestPopup.jsx'
import FlightDelayedPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/FlightDelayedPopup.jsx'
import LostBaggagePopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/LostBaggagePopup.jsx'
import AirportPickupPerksPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/AirportPickupPerksPopup.jsx'
import ComingSoonPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ComingSoonPopup.jsx'
import SuccessPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SuccessPopup.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RouteCard from '@/customComponents/editRideComponents/RouteCard.jsx'
import DateTimeCard from '@/customComponents/editRideComponents/DateTimeCard.jsx'
import {PassengersCard, ChildSeatsCard, LuggageItemsCard, OversizedCard} from '@/customComponents/editRideComponents/SummaryCards.jsx'
import CarDetailsCard from '@/customComponents/editRideComponents/CarDetailsCard.jsx'
import SpecialRequestCard from '@/customComponents/editRideComponents/SpecialRequestCard.jsx'
import PaymentMethodsCard from '@/customComponents/editRideComponents/PaymentMethodsCard.jsx'
import RideBackButton from '@/customComponents/sharedComponents/RideBackButton.jsx'
import useConfirmTrip from '@/hooks/useConfirmTrip'
import {useQueryClient} from '@tanstack/react-query'
import ReserveRideModal from '@/customComponents/editRideComponents/PageModals/ReserveRideModal.jsx'
import {tripService, isPaymentMethodUnusableError, isPayLaterNotAwaitingError, PAYMENT_METHOD_UNUSABLE_MESSAGE} from '@/services/tripService'

export default function CompletePayment() {
    const {rideId} = useParams()
    const navigate = useNavigate()
    // Confirm-specific hook: useEditTrip plus the Draft rules (own pricing pass, own policy
    // source, price-gating). See useConfirmTrip.
    const e = useConfirmTrip(rideId)
    const awaitingPayment = !!e.trip?.pay_later?.awaiting_payment
    const queryClient = useQueryClient()
    // The pay flow runs in ReserveRideModal (add card → processing → booking-received deck), the same as
    // phone-booking-confirmation. `paid` = the payment call came back fine; the trip is only refetched once the
    // modal is done, or the page would swap to the deck mid-processing.
    const [reserveOpen, setReserveOpen] = useState(false)
    const [reserveKey, setReserveKey] = useState(0)
    const [paid, setPaid] = useState(false)
    const [payError, setPayError] = useState('')
    const [showPoliciesPopup, setShowPoliciesPopup] = useState(false)

    // Nothing picked yet → the first saved card is. Local state: the hook's own card select
    // (handleCardSelect) writes to the trip, which a Pay Later booking refuses, so the raw setter is
    // what the card list gets.
    const savedCards = e.savedCards?.length ? e.savedCards : (e.trip?.passenger?.payment_methods || [])
    const hasSavedCards = savedCards.length > 0
    const firstCardId = savedCards[0]?.id
    useEffect(() => {
        if (awaitingPayment && firstCardId != null && !e.selectedCardId) e.setSelectedCardId(firstCardId)
    }, [awaitingPayment, firstCardId, e.selectedCardId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        document.body.classList.add('ride-edit-page')
        return () => document.body.classList.remove('ride-edit-page')
    }, [])

    const renderSection = () => {
        switch (e.editingSection) {
            case 'destination': return <DestinationSection mode={e.destinationPart || 'all'} destinations={e.destinations} committedDestinations={e.committedDestinations} setDestinations={e.handleDestinationsChange} onDone={() => e.handleSectionDone('destination')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError}/>
            case 'car': return <CarSection availableCars={e.availableCars} selectedCar={e.selectedCar} committedCarTypeId={e.committedCarTypeId} setSelectedCar={(car) => { e.setSelectedCar(car) }} onDone={() => e.handleSectionDone('car')} onClose={e.closeEdit} isLoading={e.isRecalculating} isRecalculating={e.isRecalculating} error={e.sectionSaveError} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} onCouponRecalculate={e.handleCouponRecalculate} defaultCouponCode={e.couponCode} breakdownCarId={e.carBreakdownId} setBreakdownCarId={e.setCarBreakdownId}/>
            case 'passengers': return <PassengerSection tripId={rideId} passengers={e.passengers} committedPassengers={e.committedPassengers} setPassengers={e.handlePassengersChange} childSeats={e.childSeats} committedChildSeats={e.committedChildSeats} setChildSeats={e.handleChildSeatsChange} passengerTypes={e.passengerTypes} childSeatTypes={e.childSeatTypes} onDone={() => e.handleSectionDone('passengers')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} view={e.passengerView} onViewChange={e.setPassengerView} only={e.passengerOnly}/>
            case 'luggage': return <LuggageSection tripId={rideId} luggage={e.luggage} committedLuggage={e.committedLuggage} setLuggage={e.handleLuggageChange} oversizedLuggage={e.oversizedLuggage} committedOversizedLuggage={e.committedOversizedLuggage} setOversizedLuggage={e.handleOversizedChange} luggageTypes={e.luggageTypes} onDone={() => e.handleSectionDone('luggage')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} oversizedMode={e.luggageOversizedMode} setOversizedMode={e.setLuggageOversizedMode}/>
            default: return null
        }
    }

    if (e.isLoading) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><SkeletonCards/></div>
    }

    if (!e.trip) {
        return <div className="py-5 pb-10"><div className="max-w-3xl mx-auto text-center p-4"><p className="text-(--text-secondary)">Trip not found</p></div></div>
    }

    if (paid && !reserveOpen) {
        return (
            <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
                <SuccessPopup isOpen trip={e.trip} isReturnFlow={e.trip?.trip_type === 'ReturnBooking'} onDone={() => navigate(`/rides/${rideId}`)}/>
            </div>
        )
    }

    // Not a Pay Later booking: nothing to complete here.
    if (!awaitingPayment && !paid) {
        return (
            <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
                <div className="max-w-3xl mx-auto text-center p-4">
                    <p className="text-(--text-secondary)">This is not a Pay Later booking.</p>
                </div>
            </div>
        )
    }

    // What the modal's processing step runs. Resolves once the call is back; `paid` / `payError` tell it how it went.
    const pay = async () => {
        setPayError('')
        const res = await tripService.payLaterPayment(rideId, {payment_method_id: e.selectedCardId})
        if (res.success || isPayLaterNotAwaitingError(res)) {
            setPaid(true)
        } else if (isPaymentMethodUnusableError(res)) {
            e.setSelectedCardId(null)
            queryClient.invalidateQueries({queryKey: ['payment-methods']})
            setPayError(res.raw?.message || PAYMENT_METHOD_UNUSABLE_MESSAGE)
        } else {
            setPayError(res.raw?.message || res.error || 'We could not save your card. Please try again.')
        }
    }

    const onComplete = () => {
        if (hasSavedCards && !e.selectedCardId) { setPayError('Please select a payment method to continue'); return }
        setPayError('')
        setReserveKey((k) => k + 1)
        setReserveOpen(true)
    }

    // Modal finished (map + minimum wait done): now the refetched trip may flip `awaiting_payment`, and the deck takes over.
    const onReserveDone = () => {
        setReserveOpen(false)
        queryClient.invalidateQueries({queryKey: ['trip', rideId]})
        queryClient.invalidateQueries({queryKey: ['trips']})
    }

    return (
        <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
            {/* Back sits at the left edge of the content column, vertically centred on
                the title block (which stays centred — hence absolute, not a flex row). */}
            <div className="max-w-3xl mx-auto text-center mb-5 relative px-14">
                <RideBackButton className="absolute left-2 top-1/2 -translate-y-1/2"/>
                <h1 className="font-bold text-2xl">Complete Payment</h1>
            </div>

            <div className="relative max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:rounded-none max-md:bg-transparent">
                <RouteCard title={e.isFromAirport ? 'From Airport' : 'Start Ride Address'} summary="origin" showBanner={false} includeStopovers={e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('route')} onToggle={() => e.toggle('route')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'originStops' : 'origin'})}/>

                <RouteCard title="Drop off Address" summary="destination" showBanner={false} includeStopovers={!e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('dropoff')} onToggle={() => e.toggle('dropoff')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'destinationOnly' : 'dropoff'})}/>

                <DateTimeCard
                    trip={e.trip} parentScheduledAt={e.trip?.parent_trip?.scheduled_at}
                    isAirportTrip={e.isAirportTrip} hideFlightField={e.isToAirport}
                    isFromAirport={e.isFromAirport} isToAirport={e.isToAirport} onRecommend={() => e.setSmartTimePopup(true)}
                    flightNumber={e.sharedFlightNumber}
                    onFlightNumberChange={e.handleFlightNumberChange}
                    onFlightClear={e.clearFlight} flightAdded={e.hasFlightNumber}
                    onSmartTime={e.handleFlightAdd}
                    applySmartTimeRef={e.applySmartTimeRef}
                    onDateTimeChange={e.setLiveDatetime} onDateChange={e.handleDateChange} onTimeChange={e.handleTimeChange}
                    dateHint={e.dateHint} setDateHint={(v) => { e.setDateHint(v); if (!v && e.isFromAirport) e.setFlightHint(true) }}
                    dateHintText="Please set the date"
                    dateTimeSectionRef={e.dateTimeSectionRef}
                    leadTimeError={e.minLeadTimeError}
                    saving={e.isSavingDateTime}
                />

                <PassengersCard trip={e.trip} expanded={e.expanded.has('passengers')} onToggle={() => e.toggle('passengers')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'passengers'})} policy={e.getFieldPolicy('passenger_types')}/>

                <ChildSeatsCard trip={e.trip} expanded={e.expanded.has('childseats')} onToggle={() => e.toggle('childseats')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'childseats'})} policy={e.getFieldPolicy('child_seats')}/>

                <LuggageItemsCard trip={e.trip} expanded={e.expanded.has('luggage')} onToggle={() => e.toggle('luggage')} onEdit={() => e.openEdit('luggage')} policy={e.getSectionPolicy('luggage')}/>

                <OversizedCard trip={e.trip} expanded={e.expanded.has('oversized')} onToggle={() => e.toggle('oversized')} onEdit={() => e.openEdit('luggage', {oversized: true})} policy={e.getSectionPolicy('luggage')}/>

                <SpecialRequestCard trip={e.trip} specialRequest={e.specialRequest} contact={e.contact} onEditSpecialRequest={() => e.setShowSRPopup(true)}/>

                <CarDetailsCard trip={e.trip} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} isRecalculating={e.isRecalculating} onEditCar={() => e.openEdit('car')}/>

                {hasSavedCards && (
                    <PaymentMethodsCard trip={e.trip} savedCards={savedCards} selectedCardId={e.selectedCardId} setSelectedCardId={e.setSelectedCardId} onCardAdded={(c) => e.setSavedCards(prev => [...prev, c])} walletAmountCents={e.trip?.wallet_amount_cents}/>
                )}

                {payError && !reserveOpen && <p className="text-red-500 text-center text-sm font-semibold mb-0!">{payError}</p>}

                <button type="button" className="hopn-add-passenger-button" onClick={onComplete}>
                    Confirm Booking
                </button>

                <p className="mb-0 text-sm text-center cursor-pointer underline" onClick={() => setShowPoliciesPopup(true)}>By proceeding, you confirm that you understand and agree to these terms.</p>
            </div>

            {/* Smart Time Popup */}
            {/* flushBody/flushTop because the screens bring their own spacing: their
                `.modalPages-spacing` gutters, and their own ModalHeader as the first
                element — the Modal's px-8 and headerless top padding sat on top of both. */}
            <Modal isOpen={e.smartTimePopup} onClose={e.closeSmartTime} className="md:max-w-157.5" flushBody flushTop>
                {/* The booking flow's recommended-pickup screens (chooser -> recommendation).
                    They render their own headers, so no ModalHeader here — the outer
                    "Smart Time Select" title belonged to the DateTimeSection this replaced. */}
                <SmartPickupFlow
                    destinations={[
                        e.committedDestinations?.origin,
                        e.committedDestinations?.destination,
                        ...(e.committedDestinations?.stopOvers || []),
                    ].filter(Boolean)}
                    rideDate={e.liveDatetime.date}
                    routeData={e.route}
                    flightNumber={e.sharedFlightNumber}
                    flightDetails={e.sharedFlightDetails}
                    onFlightChange={({flightNumber, flightDetails}) => {
                        e.setSharedFlightNumber(flightNumber);
                        e.setSharedFlightDetails(flightDetails);
                    }}
                    onSelectTime={e.handleSmartTimeSelect}
                    onClose={e.closeSmartTime}
                />
            </Modal>

            {/* Section Edit Modal */}
            <Modal isOpen={!!e.editingSection} onClose={e.closeEdit}>
                <ModalHeader
                    title={
                        e.passengerView === 'seatDetails' && e.editingSection === 'passengers' ? 'Found Right Seat'
                        : e.luggageOversizedMode && e.editingSection === 'luggage' ? 'Add Oversized'
                        : e.carBreakdownId && e.editingSection === 'car' ? 'Payment'
                        : e.editingSection === 'destination' ? ((e.destinationPart === 'origin' || e.destinationPart === 'originStops') ? (e.isFromAirport ? 'From Airport' : 'Start Ride Address') : (e.destinationPart === 'dropoff' || e.destinationPart === 'destinationOnly') ? 'Drop off Address' : 'Pick-up & Drop-off')
                        : e.editingSection === 'car' ? 'Select Vehicle'
                        : e.editingSection === 'passengers' ? (e.passengerOnly === 'childseats' ? 'Child Seats' : e.passengerOnly === 'passengers' ? 'Passengers' : 'Passengers & Child Seats')
                        : e.editingSection === 'luggage' ? 'Luggage'
                        : ''
                    }
                    onBack={
                        (e.passengerView === 'seatDetails' && e.editingSection === 'passengers') ? () => e.setPassengerView('main')
                        : (e.carBreakdownId && e.editingSection === 'car') ? () => e.setCarBreakdownId(null)
                        : undefined
                    }
                    onClose={
                        (e.carBreakdownId && e.editingSection === 'car')
                            ? undefined : e.closeEdit
                    }
                />
                {renderSection()}
            </Modal>

            <SpecialRequestPopup isOpen={e.showSRPopup} onClose={() => e.setShowSRPopup(false)} specialRequest={e.specialRequest} onSave={e.handleSpecialRequestSave}/>
            <FlightDelayedPopup isOpen={e.infoPopup === 'flightDelayed'} onClose={() => e.setInfoPopup(null)}/>
            <LostBaggagePopup isOpen={e.infoPopup === 'lostBaggage'} onClose={() => e.setInfoPopup(null)}/>
            <AirportPickupPerksPopup isOpen={e.infoPopup === 'morePerks'} onClose={() => e.setInfoPopup(null)}/>
            <ComingSoonPopup isOpen={e.infoPopup === 'findDriver'} onClose={() => e.setInfoPopup(null)} title="Find Your Driver"/>
            <ReserveRideModal key={reserveKey} isOpen={reserveOpen} onClose={() => setReserveOpen(false)} onDone={onReserveDone}
                              e={e} missing={[]} skipCard={hasSavedCards && !!e.selectedCardId}
                              submit={pay} confirmed={paid} errorMessage={payError} selectCard={e.setSelectedCardId}
                              onTerms={() => setShowPoliciesPopup(true)}/>
            <PoliciesPopup isOpen={showPoliciesPopup} onClose={() => setShowPoliciesPopup(false)} trip={e.trip} fromCalculatedPolicies/>
        </div>
    )
}
